// src/pages/ForgotPassword.jsx - Apartment Management System
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiLockLine, RiUserLine, RiErrorWarningLine, RiShieldKeyholeLine, RiEyeLine, RiEyeOffLine, RiArrowLeftLine } from 'react-icons/ri';
import { useTheme } from '../contexts/ThemeContext';
import { forgotPassword, resetPassword } from '../models/authModel';
import Navbar from './home/Navbar';
import AuthLanguageSelector from './components/AuthLanguageSelector';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const { isDarkMode, isRTL } = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState('request'); // 'request' | 'reset' | 'done'
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState(null);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleError = (err) => {
    if (err.response) {
      setError(err.response.data?.message || t('login_failed'));
    } else if (err.message) {
      setError(err.message);
    } else {
      setError(t('login_failed'));
    }
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) {
      setError(t('username_required'));
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const response = await forgotPassword(username.trim());
      setMessage(response.message || t('verification_code_sent'));
      if (response.userId) {
        setUserId(response.userId);
        setStep('reset');
      } else {
        // Generic success without a userId (account may not exist) — stay put
        // but don't reveal whether the account exists.
        setStep('reset');
      }
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otp || otp.length < 6) {
      setError(t('enter_valid_otp'));
      return;
    }
    if (newPassword.length < 8) {
      setError(t('password_min_length') || 'Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('passwords_do_not_match') || 'Passwords do not match');
      return;
    }
    if (!userId) {
      setError(t('login_failed'));
      return;
    }

    setIsLoading(true);
    try {
      await resetPassword(userId, otp, newPassword);
      setStep('done');
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = `block w-full ps-10 pe-3 py-3 ${isDarkMode ?
  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`;

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`min-h-screen flex items-center justify-center p-4 ${isDarkMode ? 'bg-zinc-950' : 'bg-white'}`}>
      <Navbar />
      <div className={`max-w-md w-full relative z-10 ${isDarkMode ? 'bg-zinc-900 border-white/[0.06]' : 'bg-white border-black/[0.1]'} border rounded-xl shadow-2xl overflow-hidden transition-all duration-300`}>
        <AuthLanguageSelector />
        <div className="p-6 sm:p-8">
          <div className="text-center mb-8">
            <div className="mb-4">
              <img
                src={isDarkMode ? "/quadcores-dark.png" : "/quadcores-light.png"}
                alt="QuadCores Logo"
                className="h-16 w-auto mx-auto" />
            </div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {t('forgot_password') || 'Forgot Password'}
            </h1>
            <p className={`mt-2 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-800'} font-light`}>
              {step === 'request' && (t('forgot_password_instructions') || 'Enter your username or email to receive a verification code')}
              {step === 'reset' && (message || t('verification_code_sent'))}
              {step === 'done' && (t('password_reset_success') || 'Your password has been reset successfully')}
            </p>
          </div>

          {error &&
          <div className={`mb-6 p-3 rounded-lg flex items-center ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-zinc-100 text-red-700'}`}>
              <RiErrorWarningLine className="me-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
          }

          {step === 'request' &&
          <form onSubmit={handleRequestSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('username')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                    <RiUserLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                    type="text"
                    dir="auto"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={inputClass}
                    placeholder={t('enter_username')} />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {isLoading ? (t('sending') || 'Sending...') : (t('send_code') || 'Send Verification Code')}
              </button>
            </form>
          }

          {step === 'reset' &&
          <form onSubmit={handleResetSubmit} className="space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('verification_code')}
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className={`block w-full px-3 py-3 text-center text-2xl tracking-widest ${isDarkMode ?
                  'bg-zinc-800 border-white/[0.1] text-white focus:border-red-500' :
                  'bg-zinc-50 border-black/[0.1] text-zinc-900 focus:border-red-500'} border rounded-lg focus:outline-none focus:ring-1 focus:ring-red-500`}
                  placeholder="123456"
                  maxLength={6} />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('new_password') || 'New Password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                    <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    dir="ltr"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`${inputClass} pe-10`}
                    placeholder={t('enter_password')} />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className={`absolute inset-y-0 end-0 pe-3 flex items-center ${isDarkMode ? 'text-zinc-500 hover:text-zinc-300' : 'text-zinc-400 hover:text-zinc-600'}`}>
                    {showPassword ? <RiEyeOffLine size={18} /> : <RiEyeLine size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-zinc-800'}`}>
                  {t('confirm_password') || 'Confirm Password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-3 flex items-center pointer-events-none">
                    <RiLockLine className={isDarkMode ? 'text-gray-500' : 'text-zinc-400'} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    dir="ltr"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass}
                    placeholder={t('confirm_password') || 'Confirm Password'} />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                <RiShieldKeyholeLine className="me-2" />
                {isLoading ? (t('verifying') || 'Verifying...') : (t('reset_password') || 'Reset Password')}
              </button>

              <button
                type="button"
                onClick={() => { setStep('request'); setError(''); }}
                className={`w-full text-sm ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-700 hover:text-zinc-950'}`}>
                {t('resend_code') || 'Resend code'}
              </button>
            </form>
          }

          {step === 'done' &&
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium flex items-center justify-center transition-colors">
              {t('back_to_login') || 'Back to Login'}
            </button>
          }

          <div className="text-center mt-6">
            <Link
              to="/login"
              className={`text-sm font-medium inline-flex items-center ${isDarkMode ? 'text-zinc-400 hover:text-white' : 'text-zinc-700 hover:text-zinc-950'}`}>
              <RiArrowLeftLine className="me-1" />
              {t('back_to_login') || 'Back to Login'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
